import RuleContainer from "@/components/RuleContainer";
import VanillaLayout from "@/layouts/VanillaLayout";
import publicPath from "@/utils/publicPath";
import usePageView from "@/hooks/usePageView";
import { NextPage } from "next";
import { NextSeo } from "next-seo";

const PrivacyPage: NextPage = (): JSX.Element => {
  usePageView();

  return (
    <VanillaLayout title="プライバシーポリシー">
      <NextSeo
        title="プライバシーポリシー"
        description="TECTUREのプライバシーポリシーのページです"
        canonical={publicPath("/privacy")}
      />
      <div className="max-w-[640px]">
        <RuleContainer
          date={
            <>
              <p>制定: 2020年6月29日</p>
              <p>改定: 2022年4月1日</p>
            </>
          }
        >
          <p>
            本プライバシーポリシーは、「TECTURE」（以下「本サービス」といいます。）における、ユーザーの個人情報を含む情報（以下「ユーザー情報」といいます。）の取扱い等について定めるものです。
            <br />
            本サービスの利用に際しては、本プライバシーポリシーの全文をお読みいただいたうえで、本プライバシーポリシーに同意いただく必要があります。
            <br />
            本プライバシーポリシーに特に定めのない限り、本プライバシーポリシーで用いる用語の定義は、「TECTURE利用規約」に従うものとします。
          </p>

          <section>
            <h2>第1条.定義</h2>
            <p>
              本プライバシーポリシーにおいて使用する以下の用語の意味は、各々以下に定めるとおりとします。
            </p>
            <dl>
              <dt>「個人情報」</dt>
              <dd>
                個人情報の保護に関する法律（以下「個人情報保護法」といいます。）第2条第1項により定義される個人情報
              </dd>
            </dl>
            <dl>
              <dt>「ユーザー情報」</dt>
              <dd>
                ユーザーの個人情報、本サービス上の行動履歴、その他ユーザーまたはユーザーの端末に関連して生成または蓄積された情報であって、本プライバシーポリシーに基づき当社が収集する情報。具体的には第2条に定めます。
              </dd>
            </dl>
          </section>

          <section>
            <h2>第2条.個人情報の適性な取得・利用</h2>
            <p>
              当社は、ユーザー情報を取得する際には、利用目的を公表または通知し（本ポリシーによる公表を含みます。）、また、直接ご本人様から契約書その他の書面（電磁的記録を含みます。）に記載された個人情報を取得する場合にはあらかじめ利用目的を明示し、適法かつ公正な手段に基づき個人情報を取得するものとし、偽りその他不正の手段により個人情報を取得することはしません。また、当社は、利用目的の達成に必要な範囲内で、適正に利用するものとし、違法または不当な行為を助長し、または誘発するおそれがある方法によりユーザ−情報を利用することはしません。
            </p>
          </section>

          <section>
            <h2>第3条.取得するユーザー情報およびその利用目的等</h2>
            <p>
              当社は、本サービスにおいて、以下の各号に定めるユーザー情報を、以下の各号に定める場合に、各号に定める目的で利用するために、ユーザーから取得します。
            </p>

            <table>
              <thead>
                <tr>
                  <th>ユーザー情報</th>
                  <th>取得時</th>
                  <th>利用目的</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>ユーザーのメールアドレスおよびパスワード</td>
                  <td>本サービスへのアカウント登録時</td>
                  <td>
                    本サービスへのアカウント登録、登録ユーザーのログイン認証を可能にするため、本サービスに関連する各種連絡・お知らせの配信その他の連絡のため、お問い合わせに対する対応のため
                    メールアドレスについては、メーカーからユーザーに対して本件製品その他の家具等のカタログまたはサンプルを送付しその他連絡するため、当該送付・連絡のために必要となるユーザーの連絡先情報をメーカーに提供するため
                  </td>
                </tr>
              </tbody>
              <tbody>
                <tr>
                  <td>
                    ユーザーの氏名、勤務先、カタログまたはサンプル送付先住所および電話番号
                  </td>
                  <td>本サービス上の問い合わせ機能使用時</td>
                  <td>
                    メーカーからユーザーに対して本件製品その他の家具等のカタログまたはサンプルを送付しその他連絡するため、当該送付・連絡のために必要となるユーザーの連絡先情報をメーカーに提供するため
                  </td>
                </tr>
              </tbody>
              <tbody>
                <tr>
                  <td>
                    インターネットドメイン名、IPアドレス、サイト内検索のクエリ情報、本プラグインの利用状況に関する情報、その他本サービス閲覧に係る情報
                  </td>
                  <td>本サービス使用時</td>
                  <td>ユーザーの利用環境分析による本サービス改善のため、</td>
                </tr>
              </tbody>
              <tbody>
                <tr>
                  <td>ユーザーの本サービス利用にかかるクッキー</td>
                  <td>本サービス使用時</td>
                  <td>
                    ユーザーの設定の保存等利便性向上のため、セッションの維持・保護等のため、ユーザーの本サービス利用回数・利用形態を把握して本サービスの円滑な利用を促進するため
                  </td>
                </tr>
              </tbody>
            </table>
          </section>

          <section>
            <h2>第4条.個人情報の安全管理等</h2>
            <ul>
              <li>
                当社は、利用目的達成のために必要な範囲内において、ユーザー情報のうち個人情報を正確かつ最新の内容に保つとともに、利用する必要がなくなったときは、当該個人情報を遅滞なく消去するよう努めるものとします。
              </li>
              <li>
                当社は、その取り扱う個人情報につき、漏えい、滅失またはき損を防止し、その他の個人情報の安全管理のために必要かつ適切な措置を講じます。
              </li>
              <li>
                当社は、その従業者に個人情報を取り扱わせるに当たって、当該個人情報の安全管理が図られるよう、当該従業者に対する必要かつ適切な監督を行うものとします。
              </li>
              <li>
                当社は、個人情報の取扱いの全部または一部を委託する場合は、その取扱いを委託された個人情報の安全管理が図られるよう、委託を受けた者に対する必要かつ適切な監督を行います。
              </li>
            </ul>
          </section>

          <section>
            <h2>第5条.第三者への提供等</h2>
            <ul>
              <li>
                当社は、ユーザー情報のうち個人情報に該当するものについて、以下の各号に定める場合を除いて、第三者に提供しません。
                <ul>
                  <li>
                    本件製品について問い合わせを行ったユーザーに本件製品その他の家具等のカタログまたはサンプルを送付しその他連絡する目的で、当社がメーカーに対して、ユーザーが本サービス上に登録したメールアドレス、ユーザーの氏名、勤務先、カタログまたはサンプル送付先住所および電話番号を提供する場合
                  </li>
                  <li>
                    第三者に提供することについてあらかじめユーザーの同意を得た場合
                  </li>
                  <li>法令に基づく場合</li>
                  <li>
                    人の生命、身体または財産の保護のために必要がある場合であって、本人の同意を得ることが困難である場合
                  </li>
                  <li>
                    公衆衛生の向上または児童の健全な育成の推進のために特に必要がある場合であって、本人の同意を得ることが困難である場合
                  </li>
                  <li>
                    国の機関もしくは地方公共団体またはその委託を受けた者が法令の定める事務を遂行することに対して協力する必要がある場合であって、本人の同意を得ることにより当該事務の遂行に支障を及ぼすおそれがある場合
                  </li>
                </ul>
              </li>
              <li>
                当社は、本サービスの改良等を目的として本サービスの利用頻度等を測定するために、外部サービスであるGoogle
                Analyticsを利用しています。Google
                AnalyticsはCookieを使用して匿名のトラフィックデータを収集しています。この行為により利用者個人が特定されることはありません。Google
                Analyticsの収集する情報およびその取扱いについては、同社のプライバシーポリシーおよび「ユーザーが
                Google パートナーのサイトやアプリを使用する際の Google
                によるデータ使用」のページ（
                <a
                  href="https://policies.google.com/technologies/partner-sites?hl=ja"
                  target="_blank"
                >
                  https://policies.google.com/technologies/partner-sites?hl=ja
                </a>
                ）をご参照ください。
              </li>
            </ul>
          </section>

          <section>
            <h2>第6条.個人情報の開示</h2>
            <ul>
              <li>
                当社は、ユーザーから、個人情報保護法の定めに基づき個人情報の開示を求められたときは、ユーザーご本人からのご請求であることを確認の上で、ユーザーに対し、遅滞なく開示を行い、または非開示の決定をしたとき、もしくは当該個人情報が存在しないときにはその旨を通知いたします。但し、個人情報保護法その他の法令により、当社が開示の義務を負わない場合は、この限りではありません。
              </li>
              <li>
                前項に定める個人情報の開示について、当社は、ユーザーに対し手数料として1件あたり1,000円を請求することができます。
              </li>
            </ul>
          </section>

          <section>
            <h2>第7条.個人情報の訂正及び利用停止等</h2>
            <ul>
              <li>
                当社は、ユーザーから、以下の各号のいずれかの請求を受けた場合は、ユーザーご本人からのご請求であることを確認の上で遅滞なく必要な調査を行い、その結果に基づき、個人情報の内容の訂正または利用停止を行い、その旨をユーザーに通知します。なお、訂正または利用停止を行わない旨の決定をしたときは、ユーザーに対しその旨を通知いたします。
                <ul>
                  <li>
                    個人情報が真実でないという理由によって個人情報保護法の定めに基づきその内容の訂正を求められた場合
                  </li>
                  <li>
                    あらかじめ公表された利用目的の範囲を超えて取扱われているという理由、違法もしくは不当な行為を助長し、もしくは誘発するおそれがある方法により個人情報を利用しているという理由、または偽りその他不正の手段により収集されたものであるという理由により、個人情報保護法の定めに基づきその利用の停止を求められた場合
                  </li>
                </ul>
              </li>
              <li>
                当社は、ユーザーから、ユーザーの個人情報について消去を求められた場合、当社が当該請求に応じる必要があると判断した場合は、ユーザーご本人からのご請求であることを確認の上で、個人情報の消去を行い、その旨をユーザーに通知します。
              </li>
              <li>
                個人情報保護法その他の法令により、当社が訂正等または利用停止等の義務を負わない場合は、第1項および第2項の規定は適用されません。
              </li>
            </ul>
          </section>

          <section>
            <h2>第8条.お問い合わせ窓口</h2>
            <p>
              ご意見、ご質問、苦情のお申出その他ユーザー情報の取扱いに関するお問い合わせは、下記の窓口までお願いいたします。
              <br />
              <a href="mailto:contact@tecture.jp">contact@tecture.jp</a>
            </p>
          </section>

          <section>
            <h2>第9条.プライバシーポリシーの変更手続</h2>
            <ul>
              <li>
                当社は、必要と判断した場合には、本プライバシーポリシーを変更することができます。
              </li>
              <li>
                本プライバシーポリシーを変更する場合、当社は、本プライバシーポリシーを変更する旨及び変更後の本プライバシーポリシーの内容並びにその効力発生時期を、本サービス上の公表その他当社所定の方法により、周知し、またはユーザーに対して通知します。但し、当社は、法令上、ユーザーの同意が必要となる本プライバシーポリシーの変更については、当社所定の方法で当該ユーザーの同意を取得します。
              </li>
            </ul>
          </section>
        </RuleContainer>
      </div>
    </VanillaLayout>
  );
};

export default PrivacyPage;
